import React, { Fragment, ReactElement, useEffect, useMemo } from 'react';
import CustomHead from '@/components/Common/Layout/Head';
import Image from 'next/image';
import Link from 'next/link';
import Script from 'next/script';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { GlobalFooter } from '@/components/Common';
import { LandingPageData } from '@/shared/types/landing-page';
import { LandingPageHero, Feature, ReviewsCarousel } from '@/components/Common/LandingPage';
import { OrganizationJsonLd } from '@/shared/types/seo';
import { RightOutlined } from '@/components/Common/Icons';
import { SEO_META } from '@/shared/seo';
import { WEBSITE_URL } from '@/shared/constants';
import { logPageView } from '@/lib/service';
import { pagePaths, pagePathsWithBuilderIO } from '@/shared/constants/landing-pages';
import { useCreateAccountProvider } from '@/lib/context';
import { useIntercom } from 'react-use-intercom';
import { useRouter } from 'next/router';
import { builder, BuilderComponent, BuilderContent } from '@builder.io/react';
import Header from '@/components/Common/Layout/Header';
import TextWithHighlight from '@/components/Home/HomepageV2/TextWithHighlight';
import FeatureCardsSection from '@/components/Home/HomepageV2/FeatureCardsSection';
import './index.less';

export const getStaticProps: GetStaticProps<{
  data: LandingPageData;
  title: string;
  path: string;
  moduleName: string;
  isBuilderIOPage?: boolean;
  content?: BuilderContent;
  commnoComponentContent?: BuilderContent;
}> = async (context) => {
  const { params } = context;
  const { path } = params;

  if (!Object(pagePaths).hasOwnProperty(String(params['path']))) {
    return {
      notFound: true,
    };
  }

  if (pagePathsWithBuilderIO.includes(path as string)) {
    const content = await builder.get(path as string, { url: path as string });
    const commnoComponentContent = await builder.get('start-company-common', { url: path as string });

    return {
      props: {
        params,
        content,
        commnoComponentContent,
        moduleName: params['path'] as string,
        ...pagePaths[String(params['path'])],
      },
    };
  }

  return {
    props: {
      params,
      moduleName: params['path'] as string,
      ...pagePaths[String(params['path'])],
    },
    revalidate: 5 * 60,
  };
};

export async function getStaticPaths() {
  // Generate the static paths for all pages in Builder
  return {
    paths: Object.keys(pagePaths).map((path) => `/l/${String(path)}`),
    fallback: 'blocking',
  };
}

const LandingPage = ({
  data,
  title,
  path,
  content,
  commnoComponentContent,
  moduleName,
  isBuilderIOPage,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const router = useRouter();
  const { boot, update, isOpen } = useIntercom();
  const { showSignupDialog } = useCreateAccountProvider();

  const isFindingClothingManufacturers = useMemo(() => moduleName === 'finding-clothing-manufacturers', [moduleName]);

  const handleRouteChange = () => {
    if (isOpen) {
      update();
    }
  };

  useEffect(() => {
    boot();
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === 'object') {
      logPageView({ pageUrl: window.location.href || '', routeName: path, pageTitle: title });
    }
  }, [path, title]);
  return (
    <Fragment>
      <LandingPageHero
        data={data}
        isBuilderIOPage={isBuilderIOPage}
        isFindingClothingManufacturers={isFindingClothingManufacturers}
        onClick={showSignupDialog}
      />
      <div className="content-body">
        <div className="content-container">
          {!isBuilderIOPage && !isFindingClothingManufacturers ? (
            <div className="content-title" style={{ marginTop: 60 }}>
              {data.features.title}
            </div>
          ) : (
            <BuilderComponent model={moduleName} content={content || null} />
          )}
          {data.features.features.map((feature, index) => (
            <Feature feature={feature} key={`feature-item-${index}`} onClick={showSignupDialog} />
          ))}
        </div>
        {isBuilderIOPage && !isFindingClothingManufacturers && (
          <BuilderComponent model="start-company-common" content={commnoComponentContent || null} />
        )}
        {!isBuilderIOPage && !isFindingClothingManufacturers && (
          <>
            <div className="content-container">
              <span className="much-more">And much more...</span>
            </div>
            <div className="content-container reviews">
              <ReviewsCarousel title={data.reviewsTitle} />
            </div>
          </>
        )}
        {isBuilderIOPage && !isFindingClothingManufacturers ? (
          <>
            <TextWithHighlight />
            <div className="spacing-before-tabs">
              <FeatureCardsSection />
            </div>
          </>
        ) : (
          <div className="content-container footer">
            <span className="content-title">{data.footer.title}</span>
            <span className="content-desc">{data.footer.description}</span>
            <div className="button-container">
              <Link href="https://www.pietrastudio.com" target="_blank" className="action-button learn-more-btn">
                Learn More
              </Link>
              <div className="action-button signup-btn" onClick={showSignupDialog}>
                {data.hero.button.text.map((item, index) => (
                  <span key={`btn-text-${index}`} className={item?.style}>
                    {item.text}
                  </span>
                ))}
                <RightOutlined />
              </div>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
};

const LandingPageHeader = ({
  pageName,
  moduleName,
  isBuilderIOPage,
}: {
  pageName: string;
  moduleName: string;
  isBuilderIOPage?: boolean;
}) => {
  const { boot, shutdown } = useIntercom();
  const { showSignupDialog } = useCreateAccountProvider();
  const structured: OrganizationJsonLd = useMemo(() => SEO_META[pageName].jsonld(), [pageName]);
  const isFindingClothingManufacturers = useMemo(() => moduleName === 'finding-clothing-manufacturers', [moduleName]);

  return (
    <Fragment>
      <Script
        id="application/ld+json"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structured) }}
      />
      {isBuilderIOPage && !isFindingClothingManufacturers ? (
        <Header showIntercom={boot} hideIntercom={shutdown} />
      ) : (
        <div className="landing-page-header">
          <Link href="/" target="_blank" className="logo">
            <Image fill src="/images/whitelogo.png" alt="Pietra" title="Pietra" loading="eager" />
          </Link>
          <div className="text-container">
            <span className="text">
              <span>Open up a free Pietra account now and become an entrepreneur today.</span>
              <span className="sign-up" onClick={showSignupDialog}>
                Sign up now
              </span>
              <RightOutlined />
            </span>
          </div>
        </div>
      )}
    </Fragment>
  );
};

LandingPage.getLayout = (page: ReactElement) => {
  const { title, description, path, moduleName, page: pageName, isBuilderIOPage } = page.props;

  return (
    <>
      <CustomHead title={title} description={description} url={`${WEBSITE_URL}/${path}`} />
      <div className="landing-page">
        {<LandingPageHeader moduleName={moduleName} isBuilderIOPage={isBuilderIOPage} pageName={pageName} />}
        {page}
        <GlobalFooter />
      </div>
    </>
  );
};

export default LandingPage;
